import { useState } from "react";
import Select from "react-select";
import moment from "moment";
import DateRangePicker from "react-bootstrap-daterangepicker";
import "bootstrap-daterangepicker/daterangepicker.css";
import { cn } from "../../../utils/classnames";
import { ChevronDownIcon, SearchIcon, SelectDropdownIndicator } from "./Icons";
import Collapse from "../../motion/Collapse";

/**
 * The booking list's filter block: a search box plus a "Filter +" toggle that
 * reveals a grey panel of fields with Apply / Clear underneath.
 *
 * Pages without extra filters can still use this for the search box alone —
 * with no children the toggle and panel are not rendered at all.
 */

const baseSelectStyles = {
  control: (base) => ({
    ...base,
    minHeight: "45px",
    borderRadius: "5px",
    borderColor: "#D9D9D9",
    boxShadow: "none",
    backgroundColor: "#FFFFFF",
    "&:hover": { borderColor: "#D9D9D9" },
  }),
  valueContainer: (base) => ({ ...base, padding: "2px 12px" }),
  singleValue: (base) => ({ ...base, color: "#282828", fontSize: "15px", lineHeight: "22px" }),
  placeholder: (base) => ({ ...base, color: "#7C7B7B", fontSize: "15px", lineHeight: "22px" }),
  multiValue: (base) => ({ ...base, backgroundColor: "#F1F1F1", borderRadius: "4px" }),
  multiValueLabel: (base) => ({ ...base, color: "#282828", fontSize: "13px" }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (base) => ({ ...base, paddingRight: "15px", color: "#282828" }),
  menu: (base) => ({ ...base, borderRadius: "5px", marginTop: "6px", zIndex: 30 }),
  // Menus render in a portal on <body> (see menuPortalProps) so they sit above
  // everything instead of being clipped by the filter panel.
  menuPortal: (base) => ({ ...base, zIndex: 1050 }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? "#F3F3F3" : "#FFFFFF",
    color: "#282828",
    cursor: "pointer",
    fontSize: "15px",
    lineHeight: "22px",
    "&:hover": { backgroundColor: "#F7F7F7" },
  }),
};

export const adminSelectStyles = baseSelectStyles;

// The filter panel animates open with overflow hidden, which would cut off a
// dropdown menu drawn inside it. Rendering the menu on <body> avoids that.
const menuPortalProps = {
  menuPortalTarget: typeof document !== "undefined" ? document.body : null,
  menuPosition: "absolute",
  menuShouldScrollIntoView: false,
};

export const FieldLabel = ({ children }) => (
  <div className="tw:mb-2 tw:text-[14px] tw:leading-5 tw:!text-[#7C7B7B]">{children}</div>
);

export const SelectField = ({ label, value, onChange, options, placeholder = "Select..." }) => (
  <div>
    {label ? <FieldLabel>{label}</FieldLabel> : null}
    <Select
      value={value}
      onChange={onChange}
      options={options}
      placeholder={placeholder}
      isSearchable={false}
      styles={baseSelectStyles}
      components={{ DropdownIndicator: SelectDropdownIndicator, IndicatorSeparator: null }}
      {...menuPortalProps}
    />
  </div>
);

export const MultiSelectField = (props) => (
  <div>
    {props.label ? <FieldLabel>{props.label}</FieldLabel> : null}
    <Select
      isMulti
      value={props.value}
      onChange={props.onChange}
      options={props.options}
      placeholder={props.placeholder || "Select..."}
      isSearchable={false}
      styles={baseSelectStyles}
      components={{ DropdownIndicator: SelectDropdownIndicator, IndicatorSeparator: null }}
      {...menuPortalProps}
    />
  </div>
);

export const DateRangeField = ({ label, dateRange, onChange, placeholder = "Select..." }) => {
  const dateText =
    dateRange?.startDate && dateRange?.endDate
      ? `${moment(dateRange.startDate).format("YYYY/MM/DD")} - ${moment(dateRange.endDate).format("YYYY/MM/DD")}`
      : "";

  return (
    <div>
      {label ? <FieldLabel>{label}</FieldLabel> : null}
      <div className="tw:relative">
        <DateRangePicker onCallback={(start, end) => onChange({ startDate: start, endDate: end })} initialSettings={dateRange}>
          <input
            type="text"
            readOnly
            value={dateText}
            placeholder={placeholder}
            className={cn(
              "tw:h-[45px] tw:w-full tw:rounded-[5px] tw:border tw:border-[#D9D9D9] tw:bg-white tw:px-[15px] tw:pr-[34px] tw:text-[15px] tw:leading-[22px]",
              dateText ? "tw:!text-[#282828]" : "tw:!text-[#7C7B7B]",
            )}
          />
        </DateRangePicker>
        <div className="tw:pointer-events-none tw:absolute tw:right-[15px] tw:top-1/2 tw:-translate-y-1/2">
          <ChevronDownIcon />
        </div>
      </div>
    </div>
  );
};

export const FilterActionButton = ({ onClick, variant = "solid", children }) => (
  <button type="button" className="tw:border-0 tw:bg-transparent tw:p-0" onClick={onClick}>
    <span
      className={cn(
        "tw:flex tw:h-[45px] tw:w-[120px] tw:items-center tw:justify-center tw:rounded-[5px] tw:text-[15px] tw:leading-[22px] tw:font-hanken-grotesk",
        variant === "solid"
          ? "tw:bg-[#282828] tw:font-bold tw:!text-white"
          : "tw:border tw:border-[#282828] tw:bg-white tw:!text-[#282828]",
      )}
    >
      {children}
    </span>
  </button>
);

const GRID_COLUMNS = {
  2: "tw:md:grid-cols-2",
  3: "tw:md:grid-cols-2 tw:xl:grid-cols-3",
  4: "tw:md:grid-cols-2 tw:xl:grid-cols-4",
};

const AdminFilterBar = ({
  search,
  setSearch,
  searchPlaceholder = "Search",
  onApply,
  onClear,
  columns = 4,
  defaultOpen = false,
  // `collapsible={false}` pins the panel open and drops the Filter toggle, for
  // pages whose filter is the point rather than an extra.
  collapsible = true,
  trailing,
  children,
}) => {
  const [showFilters, setShowFilters] = useState(defaultOpen);
  const hasPanel = !!children;
  const showToggle = hasPanel && collapsible;
  const panelOpen = hasPanel && (!collapsible || showFilters);
  const hasTopRow = Boolean(setSearch) || showToggle || Boolean(trailing);

  return (
    <section className="tw:mt-[32px] tw:flex tw:flex-col tw:gap-2">
      {hasTopRow ? (
      <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
        {setSearch ? (
          <div className="tw:flex tw:h-[45px] tw:w-full tw:max-w-[329px] tw:items-center tw:gap-[10px] tw:rounded-[5px] tw:border tw:border-[#D9D9D9] tw:bg-white tw:pl-[15px] tw:pr-[19px]">
            <SearchIcon />
            <input
              type="text"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={searchPlaceholder}
              className="tw:h-full tw:w-full tw:border-0 tw:bg-transparent tw:p-0 tw:text-[15px] tw:leading-[22px] tw:!text-[#282828] tw:outline-none"
            />
          </div>
        ) : null}

        {showToggle ? (
          <button
            type="button"
            className="tw:border-0 tw:bg-transparent tw:p-0"
            onClick={() => setShowFilters((prev) => !prev)}
          >
            <span className="tw:flex tw:h-[45px] tw:w-[108px] tw:items-center tw:justify-between tw:rounded-[5px] tw:bg-[#282828] tw:px-[17px] tw:!text-white tw:font-hanken-grotesk">
              <span className="tw:text-[15px] tw:leading-[22px]">Filter</span>
              <span className="tw:font-['Noto_Serif_JP'] tw:text-[14px] tw:font-bold">{showFilters ? "-" : "+"}</span>
            </span>
          </button>
        ) : null}

        {trailing ? <div className="tw:ml-auto tw:flex tw:items-center tw:gap-2">{trailing}</div> : null}
      </div>
      ) : null}

      <Collapse isOpen={panelOpen}>
        <div className="tw:rounded-[5px] tw:bg-[#F9F9F9] tw:p-5">
          <div className={cn("tw:grid tw:grid-cols-1 tw:gap-3", GRID_COLUMNS[columns] || GRID_COLUMNS[4])}>
            {children}
          </div>

          {onApply || onClear ? (
            <div className="tw:mt-5 tw:flex tw:items-center tw:gap-2">
              {onApply ? <FilterActionButton onClick={onApply}>Apply</FilterActionButton> : null}
              {onClear ? (
                <FilterActionButton onClick={onClear} variant="outline">
                  Clear
                </FilterActionButton>
              ) : null}
            </div>
          ) : null}
        </div>
      </Collapse>
    </section>
  );
};

export default AdminFilterBar;
